/* eslint-disable no-nested-ternary */

import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Icon, Tooltip } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { TransactionStatus } from '@features/transactions/constants';
import {
  getPaymentChannelDescription,
  getPaymentStatusDisplayText,
  getTransactionStatus,
  quickCollectStatusConfig
} from '@features/transactions/helper';
import { DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';
import { MemberTransactionType } from '@types';
import { formatISODate } from '@utils/date';

import FeeConfigAccordion from '../fee-config-accordion/FeeConfigAccordion';
import AttendanceSessionDetails from './AttendanceSessionDetails';
import InstallmentScheduleBlock from './InstallmentScheduleBlock';
import PaymentHistory from './PaymentHistory';
import PaymentSummaryStatsRow from './PaymentSummaryStatsRow';

import theme from '../../../../../themes/colors';

interface TransactionDetailsProps {
  memberDetails: MemberTransactionType;
  branchId: string;
  orderId: string;
  leadId?: string;
  /** V2 analytics only - not fired unless the caller opts in. */
  onFeeSummaryExpanded?: () => void;
}

const TransactionDetails = ({
  memberDetails,
  branchId,
  orderId,
  leadId,
  onFeeSummaryExpanded
}: TransactionDetailsProps) => {
  const { currencyConfig } = useSelector((state: any) => state.user);

  const isDefaultCurrency =
    currencyConfig?.code === DEFAULT_CURRENCY_CONFIG.code;

  const transactionStatus = getTransactionStatus(
    memberDetails.status,
    memberDetails.settlement_status
  );

  const isPaidSuccessView =
    transactionStatus === TransactionStatus.TO_SETTLE ||
    transactionStatus === TransactionStatus.SETTLED;

  const paymentChannelDescription = getPaymentChannelDescription(
    memberDetails.payment_mode,
    memberDetails.payment_method
  );

  const displayText =
    memberDetails.type === 'individual'
      ? memberDetails.group_id
        ? `${memberDetails.group_name} - ${memberDetails.note}`
        : memberDetails.note
      : memberDetails.group_name;

  const headerDisplayAmount =
    Number(memberDetails.closed_amount) > 0
      ? parseFloat(memberDetails.total_amount || '').toFixed(2)
      : parseFloat(
          (
            Number(memberDetails.total_amount) +
            Number(memberDetails.closed_amount)
          ).toString()
        ).toFixed(2);

  const totalPaid = Number(memberDetails.total_paid_amount ?? 0);
  const totalDiscount = Number(memberDetails.total_discount_amount ?? 0);
  const totalAmount = Number(memberDetails.total_amount);
  const closedAmount = Number(memberDetails.closed_amount ?? 0);
  const remainingBalance = totalAmount - totalPaid;

  const showPaymentHistory =
    Boolean(memberDetails.is_split_payment && totalPaid > 0) ||
    totalDiscount > 0 ||
    closedAmount > 0;

  const showInstallmentDetails = Boolean(memberDetails.terms?.length);

  const requestedNum = Number(memberDetails.original_amount_requested);

  const subtotalDiffersFromTotal =
    !Number.isNaN(requestedNum) &&
    Math.round(totalAmount * 100) !== Math.round(requestedNum * 100);

  const hasNonZeroFeeLines = (
    memberDetails.fee_amount_configuration ?? []
  ).some((item) => Number(item.amount) !== 0);
  const hasGst = [
    memberDetails.cgst_amount,
    memberDetails.sgst_amount,
    memberDetails.igst_amount,
    memberDetails.total_gst
  ].some((value) => Number(value ?? 0) > 0);

  const showFeeSummary =
    hasNonZeroFeeLines ||
    Number(memberDetails.fine_amount ?? 0) > 0 ||
    hasGst ||
    totalDiscount > 0 ||
    closedAmount > 0 ||
    subtotalDiffersFromTotal;

  const [transactionHistoryOpen, setTransactionHistoryOpen] = useState(false);
  const [installmentDetailsOpen, setInstallmentDetailsOpen] = useState(false);

  const statusLabel = getPaymentStatusDisplayText(
    transactionStatus,
    memberDetails.payment_mode,
    memberDetails.payment_method
  );
  const statusConfig = quickCollectStatusConfig(transactionStatus, statusLabel);

  const isNonZero = (n: number) => Math.abs(Number(n)) > 1e-9;

  const showPaymentSummaryStats =
    isNonZero(totalDiscount) ||
    (isNonZero(totalPaid) && isNonZero(remainingBalance));

  const requestedOn = formatISODate({
    date: memberDetails.requested_on,
    format: 'DD MMM YYYY',
    timeZone: 'Asia/Kolkata'
  });
  const dueDate = formatISODate({
    date: memberDetails.due_at,
    format: 'DD MMM YYYY'
  });

  return (
    <>
      <div className="px-6">
        <div
          className={`mx-auto flex w-full flex-col items-center overflow-hidden rounded-lg border border-cofeeGreyBlue5 bg-white px-4 ${
            showPaymentSummaryStats && !isPaidSuccessView ? 'py-2' : 'py-6'
          }`}
        >
          {isPaidSuccessView && (
            <div className="mb-1 flex justify-center">
              <Icon name="payment-success" size="none" className="size-15" />
            </div>
          )}
          <p
            className="max-w-96 text-center text-sm font-normal text-cofeeNeutral7"
            data-tooltip-id="user-name-payment-details"
          >
            {isPaidSuccessView ? 'Paid by' : 'To be paid by'}
          </p>
          <p className="max-w-96 truncate text-center text-lg font-semibold text-cofeeNeutral10">
            {memberDetails.name}
          </p>
          {memberDetails?.name.length > 20 && (
            <Tooltip
              id="user-name-payment-details"
              type="text"
              place="top"
              tooltipText={memberDetails?.name}
              className="max-w-78"
              variant="dark"
            />
          )}
          <p className="mb-1 max-w-96 truncate text-center text-sm font-normal text-cofeeNeutral7">
            {memberDetails.member_phone_number}
          </p>

          <div className="mt-3 flex items-center justify-center text-xl font-bold leading-tight text-cofeeNeutral10 xl:text-2xl 3xl:text-3xl">
            <CurrencyAmount
              value={Number(headerDisplayAmount)}
              className="gap-1"
              symbolClass={
                isDefaultCurrency
                  ? 'text-2xl !font-bold xl:text-3xl 3xl:text-3xl'
                  : '!text-lg lg:!text-2xl xl:!text-3xl 3xl:!text-3xl !font-bold'
              }
              valueClass="!font-bold"
            />
          </div>
          <div className="mt-3 flex min-w-35 max-w-full items-center justify-center gap-2 rounded-full border border-cofeeGreyBlue5 bg-white px-4 py-2.5 text-sm">
            {memberDetails.type === 'individual' ? (
              memberDetails.group_id ? (
                <Icon name="group-quick-collect" size="none" />
              ) : (
                <Icon name="lightning" stroke={theme.yellow40} size="none" />
              )
            ) : (
              <Icon name="users-gold" size="none" />
            )}
            <div
              className="max-w-40 truncate text-sm font-medium text-cofeeNeutral10"
              data-tooltip-id="user-name-payment-details-tooltip"
            >
              {displayText}
            </div>
            <Tooltip
              id="user-name-payment-details-tooltip"
              type="text"
              tooltipText={displayText}
              className="max-w-78"
              variant="dark"
            />
          </div>
          {!isPaidSuccessView && (
            <PaymentSummaryStatsRow
              totalPaid={totalPaid}
              totalDiscount={totalDiscount}
              remainingBalance={remainingBalance}
              isDefaultCurrency={isDefaultCurrency}
            />
          )}
        </div>
        <div className="my-3 flex flex-col gap-2 rounded-lg border border-cofeeGreyBlue5 bg-white p-4">
          {statusLabel &&
            !(!isNonZero(totalPaid) && memberDetails.status === 'success') && (
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-cofeeNeutral8">
                  Status
                </div>
                <div
                  className={`inline-flex items-center rounded-md px-3 py-1 text-sm font-medium ${statusConfig.className}`}
                >
                  {statusConfig.label}
                </div>
              </div>
            )}
          {requestedOn !== dueDate && (
            <div className="flex items-center justify-between">
              <div className="text-sm font-normal text-cofeeNeutral8">
                Requested on
              </div>
              <div className="text-sm font-normal text-cofeeNeutral10">
                {requestedOn}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="text-sm font-normal text-cofeeNeutral8">
              Due date
            </div>
            <div className="text-sm font-normal text-cofeeNeutral10">
              {dueDate}
            </div>
          </div>
          {isPaidSuccessView &&
            memberDetails.paid_at &&
            isNonZero(totalPaid) && (
              <div className="flex items-center justify-between">
                <div className="text-sm font-normal text-cofeeNeutral8">
                  Paid on
                </div>
                <div className="text-sm font-normal text-cofeeNeutral10">
                  {formatISODate({
                    date: memberDetails.paid_at,
                    format:
                      memberDetails.payment_mode === 'automatic'
                        ? 'DD MMM YYYY at hh:mm am/pm'
                        : 'DD MMM YYYY'
                  })}
                </div>
              </div>
            )}
          {isPaidSuccessView && memberDetails.settled_at && (
            <div className="flex items-center justify-between">
              <div className="text-sm font-normal text-cofeeNeutral8">
                Settled on
              </div>
              <div className="text-sm font-normal text-cofeeNeutral10">
                {formatISODate({
                  date: memberDetails.settled_at,
                  format: 'DD MMM YYYY at hh:mm am/pm'
                })}
              </div>
            </div>
          )}
          {isPaidSuccessView && memberDetails.marked_paid_at && (
            <div className="flex items-center justify-between">
              <div className="text-sm font-normal text-cofeeNeutral8">
                Marked on
              </div>
              <div className="text-sm font-normal text-cofeeNeutral10">
                {formatISODate({
                  date: memberDetails.marked_paid_at,
                  format: 'DD MMM YYYY at hh:mm am/pm'
                })}
              </div>
            </div>
          )}
          {isPaidSuccessView && memberDetails.utr && (
            <div className="flex items-center justify-between">
              <div className="text-sm font-normal text-cofeeNeutral8">UTR</div>
              <div className="text-sm font-normal text-cofeeNeutral10">
                {memberDetails.utr}
              </div>
            </div>
          )}
          {isPaidSuccessView &&
            paymentChannelDescription &&
            isNonZero(totalPaid) && (
              <div className="flex items-center justify-between gap-2">
                <div className="shrink-0 text-sm font-normal text-cofeeNeutral8">
                  Payment method
                </div>
                <div className="min-w-0 text-right text-sm font-normal text-cofeeNeutral10">
                  {paymentChannelDescription}
                </div>
              </div>
            )}
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-cofeeNeutral8">
              Order ID
            </div>
            <div className="text-sm font-normal text-cofeeNeutral10">
              {memberDetails.id}
            </div>
          </div>
          {isPaidSuccessView && memberDetails.payment_description?.trim() && (
            <div className="flex items-center justify-between gap-2">
              <div className="shrink-0 text-sm font-normal text-cofeeNeutral8">
                Notes
              </div>
              <div className="min-w-0 text-right text-sm font-normal text-cofeeNeutral10">
                {memberDetails.payment_description}
              </div>
            </div>
          )}
        </div>

        {Number(memberDetails?.no_of_sessions ?? 0) > 0 && (
          <AttendanceSessionDetails
            costPerSession={memberDetails?.cost_per_session}
            noOfSessions={memberDetails?.no_of_sessions}
          />
        )}
        {showFeeSummary && (
          <>
            <div className="pb-1 text-sm font-medium text-cofeeNeutral10">
              Fee summary
            </div>
            <FeeConfigAccordion
              feeConfig={memberDetails.fee_amount_configuration || []}
              fineAmount={memberDetails.fine_amount}
              totalAmount={memberDetails.total_amount}
              subTotalAmount={memberDetails?.original_amount_requested}
              closedAmount={memberDetails?.closed_amount}
              gstInfo={{
                cgstPercent: memberDetails?.cgst_percentage,
                sgstPercent: memberDetails?.sgst_percentage,
                igstPercent: memberDetails?.igst_percentage,
                cgstAmount: memberDetails?.cgst_amount,
                sgstAmount: memberDetails?.sgst_amount,
                igstAmount: memberDetails?.igst_amount,
                totalGst: memberDetails?.total_gst
              }}
              discountAmount={memberDetails?.total_discount_amount}
              onExpand={onFeeSummaryExpanded}
            />
          </>
        )}
        {/* Tags */}
        {memberDetails.order_tags && (
          <>
            <div className="my-2 text-base font-medium text-cofeeNeutral10">
              #Tags
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.entries(memberDetails.order_tags).map(([tag, value]) => (
                <div
                  key={tag}
                  className="rounded-full border border-cofeeGreyBlue5 px-4 py-2 text-sm font-medium text-cofeeNeutral10"
                >
                  {tag}: {value}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {(showPaymentHistory || showInstallmentDetails) && (
        <div className="mt-6 flex w-full flex-col">
          {showPaymentHistory && (
            <div className="box-border w-full border-t border-cofeeGreyBlue5">
              <button
                type="button"
                className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-cofeeGreyBlue1"
                onClick={() => setTransactionHistoryOpen((v) => !v)}
              >
                <span className="text-md font-medium text-cofeePrimary7">
                  Transaction history
                </span>
                <Icon
                  name="right"
                  size="16"
                  className={`!size-4 transition-transform duration-200 ${
                    transactionHistoryOpen ? '-rotate-90' : 'rotate-0'
                  }`}
                />
              </button>
              {transactionHistoryOpen && (
                <div className="flex flex-col gap-2 px-6 pb-4">
                  <PaymentHistory
                    branchId={branchId}
                    orderId={orderId}
                    leadId={leadId}
                    closedPayment={
                      closedAmount > 0
                        ? {
                            amount: closedAmount,
                            closedAt: memberDetails.closed_at,
                            note: memberDetails.close_payment_note
                          }
                        : undefined
                    }
                  />
                </div>
              )}
            </div>
          )}
          {showInstallmentDetails && (
            <div className="box-border w-full border-t border-cofeeGreyBlue5">
              <button
                type="button"
                className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-cofeeGreyBlue1"
                onClick={() => setInstallmentDetailsOpen((v) => !v)}
              >
                <span className="text-md font-medium text-cofeePrimary7">
                  View installment details
                </span>
                <Icon
                  name="right"
                  size="16"
                  className={`!size-4 transition-transform duration-200 ${
                    installmentDetailsOpen ? 'rotate-90' : 'rotate-0'
                  }`}
                />
              </button>
              {installmentDetailsOpen && (
                <div className="px-6 pb-4">
                  <InstallmentScheduleBlock
                    rows={memberDetails.terms ?? []}
                    isDefaultCurrency={isDefaultCurrency}
                    source="transaction-history"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default TransactionDetails;
